import { Grid, Box, Typography, Stack, FormControlLabel, Checkbox, Link } from '@mui/material';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { StickyCartBar } from '../appBars/cartBar/stickyCartBar';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { createRef, useEffect, useRef, useState } from 'react';
import { NavBar } from '../appBars/navBar/navBar';
import { useTranslation } from 'react-i18next';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { CartRoute, OrderCheckoutRoute } from '../../routes/allRoutes';
import { CartInfo } from '../shoppingCart/cartInfo';
import LoginRegister from '../shoppingCart/loginRegister';
import { PaymentMethods } from '../payment/paymentMethods';
import { CheckoutCard, CheckoutCardHeader } from './checkoutStyle';
import { DeliveryInfo } from '../shoppingCart/deliveryInfo';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import { CustomerDetails } from './customerDetails';
import { NewOrder, Order, OrderStatus, UpdateInfoRequest } from 'orderme-api-integration-client';
import { OneColumnProductCard } from '../productCard/oneColumnProductCard';
import { CustomerClient, OrderClient } from '../../helpers/client';
import { assignCustomerDetails } from '../../helpers/customerHelper';
import { clearDelivery } from '../../redux/reducers/pickupReducer';
import { PaymentStatus } from '../payment/paymentEnum';
import { PaymentDialog } from '../payment/paymentDialog';
import { openRules } from '../policy/rulesHelper';
import { DiscountCode } from './discountCode';
import { Credits } from './credits';
import { LoginDialog } from '../auth/login/loginDialog';
import { authenticateAnonymous } from '../../redux/reducers/authReducer';
import { clearCartAsync, loadCartAsync } from '../../redux/reducers/shoppingCartReducer';
import { round2 } from '@/helpers/costCounter';

let inAppBrowserRef: any;

export function Checkout() {
  const routeNavigator = useRouteNavigator();
  const dispatch = useAppDispatch();
  const ordersClient: OrderClient = new OrderClient();
  const customersClient: CustomerClient = new CustomerClient();
  const deliveryType = useAppSelector((state: RootState) => state.pickupState.deliveryType);
  const isUserLoggedIn = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const anonymousLogin = useAppSelector((state: RootState) => state.authState.anonymousLoginInfo);
  const userInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const loginInfo = useAppSelector((state: RootState) => state.customerState.loginInfo);
  const shoppingCartItems = useAppSelector((state: RootState) => state.shoppingCartState.items);
  const shoppingCartId = useAppSelector((state: RootState) => state.shoppingCartState.cartId);
  const priceToPay = useAppSelector((state: RootState) => state.shoppingCartState.priceToPay);
  const totalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalPrice);
  const deliveryPrice = useAppSelector((state: RootState) => state.shoppingCartState.deliveryPrice);
  const packsPrice = useAppSelector((state: RootState) => state.shoppingCartState.packsPrice);
  const usedCredits = useAppSelector((state: RootState) => state.shoppingCartState.usedCredits);
  const couponDiscount = useAppSelector((state: RootState) => state.shoppingCartState.couponDiscount);
  const orderDestination = useAppSelector((state: RootState) => state.pickupState.orderDestination);
  const [paymentMethod, setPaymentMethod] = useState<string>();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>();
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [closeTimeout, setCloseTimeout] = useState<number>(5000);
  const [isPaymentsDisabled, setIsPaymentDisabled] = useState<boolean>(false);
  const [isRulesApproved, setIsRulesApproved] = useState<boolean>(false);
  const [isCommunicationAgreed, setIsCommunicationAgreed] = useState<boolean>(false);
  const [openLogin, setOpenLogin] = useState(false);
  const [paymentMustBeSelected, setPaymentMustBeSelected] = useState(false);
  const { t } = useTranslation();
  const paymentTimeoutRef = useRef<number>(0);
  const exitListenerRef = useRef<(() => void) | null>(null);
  const loadStopListenerRef = useRef<((event: any) => void) | null>(null);
  const paymentsRef = createRef<any>();

  useEffect(() => {
    dispatch(loadCartAsync());
  }, [dispatch]);

  useEffect(() => {
    const isAuthenticated = loginInfo?.customers?.length;
    if (!isAuthenticated) {
      return;
    }

    if (!loginInfo.phoneConfirmed) {
      routeNavigator.goHome(true, 'popup=confirm-phone');
      return;
    }

    if (!loginInfo.emailConfirmed) {
      routeNavigator.goHome(true, 'popup=confirm-email');
      return;
    }
  }, [loginInfo, routeNavigator]);

  useEffect(() => {
    if (!deliveryType && !openPaymentDialog) {
      routeNavigator.goToCart({ replace: true });
    }
  }, [deliveryType, routeNavigator, openPaymentDialog]);

  useEffect(() => {
    if (!isRulesApproved) {
      setIsPaymentDisabled(true);
      return;
    }

    if (shoppingCartItems.length === 0) {
      setIsPaymentDisabled(true);
      return;
    }

    setIsPaymentDisabled(false);
  }, [paymentMethod, isRulesApproved, usedCredits, shoppingCartItems, isUserLoggedIn]);

  const payForOrder = async (paymentMethodId?: string) => {
    setIsPaymentDisabled(true);
    setPaymentStatus(PaymentStatus.Loading);
    setOpenPaymentDialog(true);

    const totalToPay = getTotalToPay();
    try {
      const creditsUsed = round2(Math.min(usedCredits, totalToPay));
      const paidOnlyWithCredits = totalToPay <= creditsUsed;
      let newOrder = {
        paymentMethodId: paidOnlyWithCredits ? null : paymentMethodId,
        destination: orderDestination,
        note: undefined,
        creditsUsed,
        expectedPrice: totalToPay,
        shoppingCartId: shoppingCartId,
      } as NewOrder;

      const response = await ordersClient.post(
        newOrder,
        isUserLoggedIn ? userInfo.id : null,
        anonymousLogin.isLoggedIn ? anonymousLogin.authCode : null
      );
      if (isCommunicationAgreed && isUserLoggedIn) {
        confirmCommunication();
      }
      if (paidOnlyWithCredits) {
        setCloseTimeout(4000);
        setPaymentStatus(PaymentStatus.Success);
        setOpenPaymentDialog(true);
        assignCustomerDetails();
      } else {
        getOrderInfo(response.id);
      }
    } catch (err) {
      setIsPaymentDisabled(false);
      setPaymentStatus(PaymentStatus.FailedSubmitOrder);
      console.log(err);
    }
  };

  const confirmCommunication = () => {
    let changedInfo = {
      name: userInfo.name,
      surname: userInfo.surname,
      gender: userInfo.gender,
      birthDate: userInfo.birthDate ? userInfo.birthDate : null,
      communicationAgreed: true,
    } as UpdateInfoRequest;

    customersClient.changeInfo(userInfo.id, changedInfo).catch((ex) => {
      console.error(ex);
    });
  };

  const getOrderInfo = (orderId: string) => {
    ordersClient
      .get2(orderId, isUserLoggedIn ? userInfo.id : null, anonymousLogin.isLoggedIn ? anonymousLogin.authCode : null)
      .then((response) => {
        checkForStatus(response);
      })
      .catch((error) => {
        allowPaymentsOnFailure();
        console.log(error);
      });
  };

  const checkForStatus = (orderInfo: Order) => {
    if (orderInfo.status === OrderStatus.WaitingForPayment) {
      openCheckout(orderInfo.payment.checkoutUrl);
      waitForPayment(orderInfo.id, orderInfo.payment.isActive);
      assignCustomerDetails();
    } else {
      setTimeout(function () {
        getOrderInfo(orderInfo.id);
      }, 1500);
    }
  };

  const openCheckout = (url: string) => {
    if (window.cordova) {
      inAppBrowserRef = window.cordova.InAppBrowser.open(url, '_blank', 'location=no');

      // Store listener functions in refs
      exitListenerRef.current = () => {
        handlePaymentFailure();
      };

      loadStopListenerRef.current = (event: any) => {
        const currentUrl = event.url.toLowerCase();
        if (currentUrl.includes('/failure')) {
          handlePaymentFailure();
        }
      };

      // Add listeners using the stored references
      inAppBrowserRef.addEventListener('exit', exitListenerRef.current);
      inAppBrowserRef.addEventListener('loadstop', loadStopListenerRef.current);
    } else {
      if (appStyleMode === AppStyle.Mobile) {
        window.open(url, '_blank');
      } else {
        window.location.replace(url);
      }
      dispatch(clearCartAsync());
    }
  };

  const handlePaymentFailure = () => {
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = 0;
    }
    loadPaymentInfo(PaymentStatus.Failure);
  };

  function waitForPayment(orderId: string, isActivated: boolean) {
    let retryCount = 0;
    const MAX_RETRIES = 300; // 10 minutes max (300 * 2 seconds)

    async function continueWaiting() {
      // Clear any existing timeout
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = 0;
      }

      // Check max retries
      if (retryCount >= MAX_RETRIES) {
        console.error('Payment timeout: Maximum retries exceeded');
        loadPaymentInfo(PaymentStatus.Failure);
        return;
      }

      retryCount++;

      try {
        const customerId = isUserLoggedIn ? userInfo.id : null;
        const authCode = anonymousLogin.isLoggedIn ? anonymousLogin.authCode : null;

        const payment = await ordersClient.getPayment(orderId, customerId, authCode);

        // If payment is not activated yet, wait until it's activated
        if (!isActivated) {
          isActivated = payment.isActive;
          paymentTimeoutRef.current = setTimeout(continueWaiting, 2000) as any;
          return;
        }

        // Payment is still active, continue waiting
        if (payment.isActive) {
          paymentTimeoutRef.current = setTimeout(continueWaiting, 2000) as any;
          return;
        }

        // Payment completed - check result
        if (payment.paidOn) {
          loadPaymentInfo(PaymentStatus.Success);
        } else {
          loadPaymentInfo(PaymentStatus.Failure);
        }
      } catch (error) {
        console.error('Error fetching payment info:', error);
        // On error, retry with exponential backoff for first few attempts
        const delay = retryCount < 5 ? 2000 * Math.min(retryCount, 3) : 2000;
        paymentTimeoutRef.current = setTimeout(continueWaiting, delay) as any;
      }
    }

    continueWaiting();
  }

  const loadPaymentInfo = (status: PaymentStatus) => {
    setPaymentStatus(status);
    setOpenPaymentDialog(true);

    if (window.cordova && inAppBrowserRef) {
      // Remove listeners using the stored references
      if (exitListenerRef.current) {
        inAppBrowserRef.removeEventListener('exit', exitListenerRef.current);
        exitListenerRef.current = null;
      }
      if (loadStopListenerRef.current) {
        inAppBrowserRef.removeEventListener('loadstop', loadStopListenerRef.current);
        loadStopListenerRef.current = null;
      }

      setTimeout(function () {
        inAppBrowserRef.close();
      }, 4000);
    }
  };

  function getTotalToPay(): number {
    return round2(totalPrice + deliveryPrice + packsPrice - couponDiscount);
  }

  useEffect(() => {
    if (paymentMustBeSelected) {
      paymentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [paymentMustBeSelected, paymentsRef]);

  const buy = async () => {
    if (!isUserLoggedIn && !anonymousLogin.isLoggedIn) {
      setOpenLogin(true);
      return;
    }

    if (!paymentMethod && priceToPay > 0) {
      if (paymentMustBeSelected) {
        paymentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        setPaymentMustBeSelected(true);
      }
      return;
    }

    if (anonymousLogin.isLoggedIn) {
      try {
        var result = await dispatch(authenticateAnonymous(anonymousLogin.authCode)).unwrap();
        if (result.verified) {
          payForOrder(paymentMethod);
        }
      } catch {
        console.warn('Invalid confirmation code. Require to login or confirm phone once again.');
      }
    } else {
      payForOrder(paymentMethod);
    }
  };

  const closePaymentDialog = () => {
    if (paymentStatus !== PaymentStatus.FailedSubmitOrder) {
      dispatch(clearDelivery());
      dispatch(clearCartAsync());
    }
    if (paymentStatus == PaymentStatus.Failure) {
      handleNavigateToOrderHistory();
    } else if (paymentStatus == PaymentStatus.Success) {
      assignCustomerDetails();
      handleNavigateHome();
    } else {
      setOpenPaymentDialog(false);
    }
  };

  const handleNavigateHome = () => {
    setTimeout(() => {
      routeNavigator.goHome(true);
    }, 500);
  };

  const handleNavigateToOrderHistory = () => {
    setTimeout(() => {
      routeNavigator.goHome(true);
      routeNavigator.goToOrderHistory();
    }, 500);
  };

  const allowPaymentsOnFailure = () => {
    setIsPaymentDisabled(false);
    setPaymentStatus(PaymentStatus.Failure);
  };

  const toggleApproveRules = () => {
    setIsRulesApproved(!isRulesApproved);
  };

  const toggleAgreeCommunication = () => {
    setIsCommunicationAgreed(!isCommunicationAgreed);
  };

  const closeLogin = () => setOpenLogin(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NavBar barTitle={t('payment:orderCheckout')} />
      <BreadcrumbsBar paths={[{ path: CartRoute, isLink: true }, { path: OrderCheckoutRoute }]} />
      <Grid
        className={appStyleMode === AppStyle.Mobile && 'checkout-container'}
        sx={{
          width: '100%',
          backgroundColor: 'white',
          color: 'black',
          borderRadius: '15px',
          p: '10px',
        }}
      >
        <Typography fontWeight="bold" variant="h6" sx={{ mb: '10px', ml: '10px' }}>
          {t('payment:orderCheckout')}
        </Typography>
        <Grid container spacing={{ xs: 0, md: 2 }}>
          <Grid size={{ xs: 12, md: 'grow', lg: 4 }}>
            {!isUserLoggedIn && (
              <Box sx={{ mb: '20px' }}>
                <LoginRegister />
              </Box>
            )}
            {isUserLoggedIn && (
              <CheckoutCard>
                <CheckoutCardHeader>{t('payment:customerInfo')}</CheckoutCardHeader>
                <CustomerDetails />
              </CheckoutCard>
            )}
            {anonymousLogin.isLoggedIn && (
              <CheckoutCard>
                <CheckoutCardHeader>{t('payment:customerInfo')}</CheckoutCardHeader>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('account:phoneNumber')}
                  </Typography>
                  <Typography variant="body2">{anonymousLogin.phone || '-'}</Typography>
                </Box>
              </CheckoutCard>
            )}
            <CheckoutCard>
              <Box sx={{ display: 'flex' }}>
                <CheckoutCardHeader>{t('payment:deliveryDetails')}</CheckoutCardHeader>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    ml: 'auto',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    gap: 0.5,
                    color: 'secondary.main',
                  }}
                  onClick={() => routeNavigator.goToWhereTo()}
                >
                  <EditLocationAltIcon fontSize="small" />
                  <Typography variant="body2">{t('payment:changeDeliveryDetails')}</Typography>
                </Box>
              </Box>
              <DeliveryInfo />
            </CheckoutCard>
          </Grid>
          <Grid ref={paymentsRef} sx={{ scrollMargin: '200px' }} size={{ xs: 12, md: 'grow', lg: 4 }}>
            <CheckoutCard
              sx={{
                borderColor: paymentMustBeSelected && !paymentMethod && 'error.main',
                borderWidth: paymentMustBeSelected && !paymentMethod && 2,
                borderStyle: paymentMustBeSelected && !paymentMethod && 'solid',
              }}
            >
              <CheckoutCardHeader>{t('payment:paymentMethods')}</CheckoutCardHeader>
              <PaymentMethods
                isDisabled={false}
                onPaymentSelected={(id) => {
                  setPaymentMethod(id);
                  setPaymentMustBeSelected(false);
                }}
              />
            </CheckoutCard>
          </Grid>
          <Grid size={{ xs: 12, md: 'auto', lg: 4 }}>
            <Box sx={{ width: { md: 320, lg: '100%' } }}>
              <CheckoutCard sx={{ backgroundColor: '#f5f5f5', px: '10px' }}>
                <CheckoutCardHeader sx={{ pl: '10px' }}>{t('payment:orderSummary')}</CheckoutCardHeader>
                <Stack spacing={2}>
                  {shoppingCartItems.map((item) => (
                    <OneColumnProductCard item={item.serviceItem} count={item.count} removable={item.removable} />
                  ))}
                </Stack>
              </CheckoutCard>

              <Box sx={{ mb: '20px' }}>
                <CartInfo isPurchase />
              </Box>

              <CheckoutCard>
                <Stack spacing={2}>
                  {isUserLoggedIn && userInfo?.credit > 0 && <Credits shoppingCartItems={shoppingCartItems} />}
                  <DiscountCode />
                </Stack>
              </CheckoutCard>

              <FormControlLabel
                sx={{
                  ml: '0px',
                  mb: '10px',
                }}
                control={
                  <Checkbox
                    sx={{ '& .MuiSvgIcon-root': { fontSize: '20px', color: 'secondary.main' } }}
                    checked={isRulesApproved}
                    onChange={toggleApproveRules}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" display="inline">
                      {t('payment:agreeECommerceRulesPart1')}
                    </Typography>
                    <Typography variant="body2" display="inline">
                      <Link
                        component="button"
                        underline="hover"
                        sx={{ verticalAlign: 'baseline' }}
                        onClick={() => openRules(routeNavigator.getRulesPath())}
                      >
                        {t('payment:agreeECommerceRulesPart2')}
                      </Link>
                    </Typography>
                  </Box>
                }
              />
              {isUserLoggedIn && (
                <FormControlLabel
                  sx={{
                    ml: '0px',
                    mb: '10px',
                  }}
                  control={
                    <Checkbox
                      sx={{ '& .MuiSvgIcon-root': { fontSize: '20px', color: 'secondary.main' } }}
                      checked={isCommunicationAgreed}
                      onChange={toggleAgreeCommunication}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" display="inline">
                        {t('payment:agreeCommunication')}
                      </Typography>
                    </Box>
                  }
                />
              )}

              <Box>
                <StickyCartBar open={buy} isPurchase={true} isDisabled={isPaymentsDisabled} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <PaymentDialog
        open={openPaymentDialog}
        onClose={closePaymentDialog}
        paymentStatus={paymentStatus}
        autoCloseDelay={closeTimeout}
      />
      <LoginDialog open={openLogin} onClose={closeLogin} allowAnonymous />
    </Box>
  );
}
